import { useState, useMemo } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TableContainer,
  TextField,
  Button,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Autocomplete,
  Tooltip,
  InputAdornment,
  Snackbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SimCardIcon from '@mui/icons-material/SimCard';
import FilterListIcon from '@mui/icons-material/FilterList';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import { useEffectAsync } from '../../reactHelper';

// Country codes with flags
const COUNTRY_INFO = {
  BR: { name: 'Brasil', flag: '🇧🇷' },
  PT: { name: 'Portugal', flag: '🇵🇹' },
  US: { name: 'Estados Unidos', flag: '🇺🇸' },
  MX: { name: 'México', flag: '🇲🇽' },
  AR: { name: 'Argentina', flag: '🇦🇷' },
  CL: { name: 'Chile', flag: '🇨🇱' },
  CO: { name: 'Colômbia', flag: '🇨🇴' },
  PE: { name: 'Peru', flag: '🇵🇪' },
  ES: { name: 'Espanha', flag: '🇪🇸' },
  GB: { name: 'Reino Unido', flag: '🇬🇧' },
  DE: { name: 'Alemanha', flag: '🇩🇪' },
  FR: { name: 'França', flag: '🇫🇷' },
  IT: { name: 'Itália', flag: '🇮🇹' },
  NL: { name: 'Holanda', flag: '🇳🇱' },
  PL: { name: 'Polônia', flag: '🇵🇱' },
  RU: { name: 'Rússia', flag: '🇷🇺' },
  IN: { name: 'Índia', flag: '🇮🇳' },
  CN: { name: 'China', flag: '🇨🇳' },
  ID: { name: 'Indonésia', flag: '🇮🇩' },
  TH: { name: 'Tailândia', flag: '🇹🇭' },
  ZA: { name: 'África do Sul', flag: '🇿🇦' },
  NG: { name: 'Nigéria', flag: '🇳🇬' },
  AU: { name: 'Austrália', flag: '🇦🇺' },
  NZ: { name: 'Nova Zelândia', flag: '🇳🇿' },
  GLOBAL: { name: 'Global', flag: '🌍' },
};

const ApnConfigTab = ({ t }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [apnConfigs, setApnConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState(null);
  const [operatorFilter, setOperatorFilter] = useState(null);
  const [countries, setCountries] = useState([]);
  const [operators, setOperators] = useState([]);
  const [editDialog, setEditDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchApns = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (countryFilter) params.append('country', countryFilter);
      if (operatorFilter) params.append('operator', operatorFilter);

      const response = await fetch(`/api/apn-config?${params}`);
      if (response.ok) {
        const data = await response.json();
        setApnConfigs(data);
      }
    } catch (e) {
      console.error('Error fetching APNs:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [countriesRes, operatorsRes] = await Promise.all([
        fetch('/api/apn-config/countries'),
        fetch('/api/apn-config/operators'),
      ]);
      if (countriesRes.ok) {
        setCountries(await countriesRes.json());
      }
      if (operatorsRes.ok) {
        setOperators(await operatorsRes.json());
      }
    } catch (e) {
      console.error('Error fetching filters:', e);
    }
  };

  useEffectAsync(async () => {
    await fetchFilters();
    await fetchApns();
  }, []);

  useEffectAsync(async () => {
    await fetchApns();
  }, [countryFilter, operatorFilter]);

  const filteredApns = useMemo(() => {
    if (!search) return apnConfigs;
    const searchLower = search.toLowerCase();
    return apnConfigs.filter((apn) =>
      apn.name?.toLowerCase().includes(searchLower) ||
      apn.apnname?.toLowerCase().includes(searchLower) ||
      apn.operator?.toLowerCase().includes(searchLower)
    );
  }, [apnConfigs, search]);

  const paginatedApns = useMemo(() =>
    filteredApns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
  [filteredApns, page, rowsPerPage]);

  const handleEdit = (item) => {
    setEditItem(item ? { ...item } : {
      name: '',
      apnname: '',
      apnusername: '',
      apnpassword: '',
      country: 'BR',
      operator: '',
      active: true,
    });
    setEditDialog(true);
  };

  const handleSave = async () => {
    try {
      const isNew = !editItem.id;
      const url = isNew ? '/api/apn-config' : `/api/apn-config/${editItem.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editItem),
      });

      if (response.ok) {
        setSnackbar({ open: true, message: t('sharedSaved'), severity: 'success' });
        setEditDialog(false);
        fetchApns();
        fetchFilters();
      } else {
        throw new Error('Failed to save');
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`/api/apn-config/${deleteItem.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSnackbar({ open: true, message: t('sharedRemoved'), severity: 'success' });
        setDeleteDialog(false);
        fetchApns();
        fetchFilters();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleCopy = (apn) => {
    const text = apn.apnusername
      ? `${apn.apnname} (${apn.apnusername}/${apn.apnpassword || ''})`
      : apn.apnname;
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: t('sharedCopied'), severity: 'info' });
  };

  const getCountryInfo = (code) => COUNTRY_INFO[code] || { name: code, flag: '🏳️' };

  const clearFilters = () => {
    setCountryFilter(null);
    setOperatorFilter(null);
    setSearch('');
    setPage(0);
  };

  const hasFilters = countryFilter || operatorFilter || search;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SimCardIcon color="primary" />
            <Typography variant="h6">
              {t('configApn')}
            </Typography>
            <Chip
              size="small"
              label={`${filteredApns.length} ${t('sharedTotal').toLowerCase()}`}
              color="primary"
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchApns}
              disabled={loading}
              size={isMobile ? 'small' : 'medium'}
            >
              {!isMobile && t('sharedRefresh')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleEdit(null)}
              size={isMobile ? 'small' : 'medium'}
            >
              {!isMobile && t('sharedAdd')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('sharedSearch')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <Autocomplete
              size="small"
              fullWidth
              options={countries}
              value={countryFilter}
              onChange={(_, value) => { setCountryFilter(value); setPage(0); }}
              getOptionLabel={(option) => {
                const info = getCountryInfo(option);
                return `${info.flag} ${info.name}`;
              }}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <Box component="li" key={option} {...rest}>
                    <Typography>
                      {getCountryInfo(option).flag} {getCountryInfo(option).name}
                    </Typography>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('apnCountry')} />
              )}
              slotProps={{
                paper: { sx: { minWidth: 280 } },
                listbox: { sx: { maxHeight: 350 } },
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <Autocomplete
              size="small"
              fullWidth
              options={operators}
              value={operatorFilter}
              onChange={(_, value) => { setOperatorFilter(value); setPage(0); }}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <Box component="li" key={option} {...rest}>
                    {option}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('apnOperator')} />
              )}
              slotProps={{
                paper: { sx: { minWidth: 250 } },
                listbox: { sx: { maxHeight: 350 } },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={clearFilters}
              disabled={!hasFilters}
              color={hasFilters ? 'primary' : 'inherit'}
            >
              {t('reportClear')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredApns.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            {t('sharedNoData')}
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                    <TableCell>{t('apnCountry')}</TableCell>
                    <TableCell>{t('apnOperator')}</TableCell>
                    {!isMobile && <TableCell>{t('sharedName')}</TableCell>}
                    <TableCell>{t('apnName')}</TableCell>
                    {!isMobile && <TableCell>{t('apnUsername')}</TableCell>}
                    <TableCell>{t('sharedStatus')}</TableCell>
                    <TableCell align="right">{t('sharedActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedApns.map((apn) => {
                    const countryInfo = getCountryInfo(apn.country);
                    return (
                      <TableRow
                        key={apn.id}
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'action.hover',
                          },
                        }}
                      >
                        <TableCell>
                          <Tooltip title={countryInfo.name}>
                            <Chip
                              size="small"
                              label={`${countryInfo.flag} ${apn.country}`}
                              variant="outlined"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {apn.operator}
                          </Typography>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Typography variant="body2">
                              {apn.name}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                              }}
                            >
                              {apn.apnname}
                            </Typography>
                            <IconButton size="small" onClick={() => handleCopy(apn)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            {apn.apnusername ? (
                              <Typography variant="caption" color="text.secondary">
                                {apn.apnusername}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.disabled">-</Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Chip
                              size="small"
                              label={apn.active ? t('sharedActive') : t('sharedDisabled')}
                              color={apn.active ? 'success' : 'default'}
                            />
                            {apn.edited && (
                              <Chip
                                size="small"
                                label={t('configEdited')}
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleEdit(apn)} color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => { setDeleteItem(apn); setDeleteDialog(true); }}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredApns.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              labelRowsPerPage={t('sharedRowsPerPage')}
              rowsPerPageOptions={[10, 15, 25, 50]}
            />
          </>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          {editItem?.id ? t('sharedEdit') : t('sharedAdd')} - {t('configApn')}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                fullWidth
                options={Object.keys(COUNTRY_INFO)}
                value={editItem?.country || 'BR'}
                onChange={(_, value) => setEditItem({ ...editItem, country: value })}
                getOptionLabel={(option) => {
                  const info = getCountryInfo(option);
                  return `${info.flag} ${info.name}`;
                }}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <Box component="li" key={option} {...rest}>
                      <Typography>
                        {getCountryInfo(option).flag} {getCountryInfo(option).name}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('apnCountry')} required />
                )}
                slotProps={{
                  paper: { sx: { minWidth: 300 } },
                  listbox: { sx: { maxHeight: 350 } },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('apnOperator')}
                value={editItem?.operator || ''}
                onChange={(e) => setEditItem({ ...editItem, operator: e.target.value })}
                required
                placeholder="Ex: Vivo, Claro, TIM"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('sharedName')}
                value={editItem?.name || ''}
                onChange={(e) => setEditItem({ ...editItem, name: e.target.value })}
                required
                helperText="Ex: Vivo Brasil (M2M)"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('apnName')}
                value={editItem?.apnname || ''}
                onChange={(e) => setEditItem({ ...editItem, apnname: e.target.value })}
                required
                helperText="Ex: zap.vivo.com.br"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('apnUsername')}
                value={editItem?.apnusername || ''}
                onChange={(e) => setEditItem({ ...editItem, apnusername: e.target.value })}
                placeholder={t('sharedOptional')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('apnPassword')}
                value={editItem?.apnpassword || ''}
                onChange={(e) => setEditItem({ ...editItem, apnpassword: e.target.value })}
                placeholder={t('sharedOptional')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          <Button onClick={() => setEditDialog(false)}>
            {t('sharedCancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!editItem?.name || !editItem?.apnname || !editItem?.operator}
          >
            {t('sharedSave')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          {t('sharedRemoveConfirm')}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          <Typography>
            {deleteItem?.name} ({deleteItem?.apnname})
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          <Button onClick={() => setDeleteDialog(false)}>
            {t('sharedCancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('sharedRemove')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApnConfigTab;
